import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import auroraBg from '../../../assets/aurora.webp';
import { AuthContext } from '../../../providers/AuthProvider';
import { useWorkspaceStore } from './workspace.store';
import { Workspace, effectiveWorkspace, orderWorkspaces, workspaceById } from './workspaces';

/**
 * A small window onto the aurora, turned to a workspace's hue.
 *
 * The same artwork the rail is washed with, so each option in the menu is a
 * preview of what the rail will look like — the colour is the workspace's
 * identity, and a separate icon set would be a second one to learn.
 */
export const WorkspaceSwatch = ({ hue, className }: { hue: number; className?: string }) => (
    <span
        aria-hidden
        className={cn('inline-block shrink-0 rounded-md bg-cover ring-1 ring-black/5 transition-[filter] duration-500', className)}
        style={{
            backgroundImage: `url(${auroraBg})`,
            backgroundPosition: '40% 30%',
            backgroundSize: '260%',
            filter: `hue-rotate(${hue}deg) saturate(1.35)`,
        }}
    />
);

/**
 * Choose which part of the backoffice the rail shows.
 *
 * Choosing one opens its first page, because a rail that changed while the
 * page behind it stayed on another area would contradict itself.
 */
export function WorkspaceSwitcher() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const current = useWorkspaceStore((s) => s.workspace);
    const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
    const goals = useWorkspaceStore((s) => s.goals);
    // A manufacturer's account has its own set (workspaces.ts › workspacesFor).
    const kind = (useContext(AuthContext)?.user as any)?.vendor?.kind;
    const active = workspaceById(effectiveWorkspace(current, kind));
    const { focus, more } = orderWorkspaces(goals, kind);

    const item = (workspace: Workspace) => (
        <DropdownMenuItem
            key={workspace.id}
            className="items-start gap-3 py-2"
            onSelect={() => {
                setWorkspace(workspace.id);
                navigate(workspace.home);
            }}
        >
            <WorkspaceSwatch hue={workspace.hue} className="mt-0.5 size-8 rounded-lg" />
            <span className="flex min-w-0 flex-col">
                <span className="text-sm font-medium">{t(workspace.labelKey, workspace.label)}</span>
                <span className="text-xs text-muted-foreground">{t(workspace.descriptionKey, workspace.description)}</span>
            </span>
            {workspace.id === active.id && <Check className="ms-auto mt-1 size-4 shrink-0" />}
        </DropdownMenuItem>
    );

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="relative z-10 flex h-8 min-w-0 max-w-full items-center gap-2 rounded-lg bg-white/45 px-2 text-start ring-1 ring-white/60 backdrop-blur-sm transition-colors hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-white/5 dark:ring-white/10 dark:hover:bg-white/10"
                    aria-label={t('WORKSPACE.SWITCH', 'Switch workspace') as string}
                >
                    <WorkspaceSwatch hue={active.hue} className="size-4 rounded" />
                    <span className="truncate text-sm font-medium text-foreground">{t(active.labelKey, active.label)}</span>
                    <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" sideOffset={6} className="w-72">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    {more.length > 0 ? t('ONBOARDING_V2.WORKSPACES.YOURS') : t('WORKSPACE.TITLE', 'Workspaces')}
                </DropdownMenuLabel>
                <DropdownMenuGroup>{focus.map(item)}</DropdownMenuGroup>
                {more.length > 0 && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('ONBOARDING_V2.WORKSPACES.MORE')}</DropdownMenuLabel>
                        <DropdownMenuGroup>{more.map(item)}</DropdownMenuGroup>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
